import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Loading, Modal, TextInput, useToast } from '../ui';

interface Profile {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  memberSince: string;
  lastLoginAt: string | null;
  twoStepOn: boolean;
}

interface SessionRow {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  lastUsedAt: string;
  current: boolean;
}

/** "Chrome on Windows" from a user agent, roughly - enough to recognise a device. */
function describeAgent(agent: string | null): string {
  if (!agent) return 'Unknown device';
  const browser = /Edg\//.test(agent)
    ? 'Edge'
    : /Chrome\//.test(agent)
      ? 'Chrome'
      : /Firefox\//.test(agent)
        ? 'Firefox'
        : /Safari\//.test(agent)
          ? 'Safari'
          : 'Browser';
  const os = /Android/.test(agent)
    ? 'Android'
    : /iPhone|iPad/.test(agent)
      ? 'iOS'
      : /Windows/.test(agent)
        ? 'Windows'
        : /Mac OS/.test(agent)
          ? 'macOS'
          : /Linux/.test(agent)
            ? 'Linux'
            : 'a device';
  return `${browser} on ${os}`;
}

/**
 * A person's own account: the details that are theirs to change, and every
 * place they are signed in, with a way to sign any of them out. Email, role
 * and property access are an owner's to change, not editable here.
 */
export function AccountModal({ onClose }: { onClose: () => void }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const profile = useQuery({ queryKey: ['me-profile'], queryFn: () => api.get<Profile>('/api/auth/me/profile') });
  const sessions = useQuery({ queryKey: ['me-sessions'], queryFn: () => api.get<SessionRow[]>('/api/auth/me/sessions') });

  const [name, setName] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.patch<Profile>('/api/auth/me/profile', {
        ...(name !== null ? { name } : {}),
        ...(phone !== null ? { phone } : {}),
      }),
    onSuccess: () => {
      push('Saved', 'ok');
      setName(null);
      setPhone(null);
      void queryClient.invalidateQueries({ queryKey: ['me-profile'] });
    },
    onError: fail,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete<{ current: boolean }>(`/api/auth/me/sessions/${id}`),
    onSuccess: (result) => {
      if (result.current) {
        window.location.assign('/sign-in');
        return;
      }
      push('Signed out there', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['me-sessions'] });
    },
    onError: fail,
  });

  return (
    <Modal title="My account" onClose={onClose}>
      {profile.isLoading || !profile.data ? (
        <Loading />
      ) : (
        <div className="col gap-14">
          <div className="form-grid">
            <TextInput label="Name" value={name ?? profile.data.name} onChange={(e) => setName(e.target.value)} />
            <TextInput label="Phone" value={phone ?? profile.data.phone ?? ''} onChange={(e) => setPhone(e.target.value)} />
            <TextInput label="Email" value={profile.data.email} disabled hint="Your sign-in. Ask an owner to change it." />
          </div>
          <div>
            <button
              className="btn primary sm"
              disabled={save.isPending || (name === null && phone === null) || name === ''}
              onClick={() => save.mutate()}
            >
              Save
            </button>
          </div>

          <div className="small muted">
            How SARTHI handles your details: <a href="/privacy" target="_blank" rel="noreferrer">privacy policy</a>.
            Your name, email, role and sign-ins are what this account holds about you.
          </div>

          <div className="b">Where you are signed in</div>
          {sessions.isLoading ? (
            <Loading />
          ) : (
            <div className="table-wrap">
              <table className="tbl tbl-stack">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Address</th>
                    <th>Last used</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(sessions.data ?? []).map((row) => (
                    <tr key={row.id}>
                      <td>
                        {describeAgent(row.userAgent)}
                        {row.current ? <span className="chip ok" style={{ marginLeft: 6 }}>this one</span> : null}
                      </td>
                      <td className="mono xs">{row.ip ?? '—'}</td>
                      <td>{dateTime(row.lastUsedAt)}</td>
                      <td>
                        <button className="btn sm" disabled={revoke.isPending} onClick={() => revoke.mutate(row.id)}>
                          Sign out
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
